import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  ApiError,
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  SpecificationDto,
  SpecificationEditDto
} from 'src/store/specifications/specification';
import { alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum SpecificationTypes {
  FetchAll = 'specifications/FetchAll',
  FetchFiltered = 'specifications/FetchFiltered',
  Add = 'specifications/Add',
  Update = 'specifications/Update',
  Delete = 'specifications/Delete',
  Failure = 'specifications/Failure',
  UpdateRequestState = 'specifications/UpdateRequestState'
}

export type SpecificationAction =
  | { type: SpecificationTypes.FetchAll; specifications: SpecificationDto[] }
  | {
      type: SpecificationTypes.FetchFiltered;
      filteredSpecifications: DataResult;
    }
  | { type: SpecificationTypes.Add; specification: SpecificationDto }
  | { type: SpecificationTypes.Update; specification: SpecificationDto }
  | { type: SpecificationTypes.Delete; specification: SpecificationDto }
  | { type: SpecificationTypes.Failure; errors: ApiError }
  | {
      type: SpecificationTypes.UpdateRequestState;
      requestState: DataSourceRequestState;
    };

export const specificationActions = {
  fetchAll: (specifications: SpecificationDto[]): SpecificationAction => ({
    type: SpecificationTypes.FetchAll,
    specifications
  }),
  fetchFiltered: (filteredSpecifications: DataResult): SpecificationAction => ({
    type: SpecificationTypes.FetchFiltered,
    filteredSpecifications
  }),
  add: (specification: SpecificationDto): SpecificationAction => ({
    type: SpecificationTypes.Add,
    specification
  }),
  update: (specification: SpecificationDto): SpecificationAction => ({
    type: SpecificationTypes.Update,
    specification
  }),
  delete: (specification: SpecificationDto): SpecificationAction => ({
    type: SpecificationTypes.Delete,
    specification
  }),
  requestFailure: (errors: any): SpecificationAction => ({
    type: SpecificationTypes.Failure,
    errors
  }),
  updateRequestState: (
    requestState: DataSourceRequestState
  ): SpecificationAction => ({
    type: SpecificationTypes.UpdateRequestState,
    requestState
  })
};

export const fetchSpecifications = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(SpecificationTypes.FetchAll));
    const { ok, data, error } = await getRequest<SpecificationDto[]>(
      '/specifications'
    );
    dispatch(requestEnd(SpecificationTypes.FetchAll));
    return ok
      ? dispatch(specificationActions.fetchAll(data))
      : dispatch(specificationActions.requestFailure(error));
  };
};

export const fetchFilteredSpecifications = (queryStr: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(SpecificationTypes.FetchFiltered));
    const { ok, data, error } = await getRequest<DataResult>(
      `/specifications/filtered?${queryStr}`
    );
    dispatch(requestEnd(SpecificationTypes.FetchFiltered));
    return ok
      ? dispatch(specificationActions.fetchFiltered(data))
      : dispatch(specificationActions.requestFailure(error));
  };
};

export const createSpecification = (spec: SpecificationEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/specifications', spec);
    return ok
      ? dispatch(alertSuccess(i18n.t('specification.created')))
      : dispatch(specificationActions.requestFailure(error));
  };
};

export const editSpecification = (id: number, spec: SpecificationEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/specifications/${id}`, spec);
    return ok
      ? dispatch(alertSuccess(i18n.t('specification.updated')))
      : dispatch(specificationActions.requestFailure(error));
  };
};

export const deleteSpecification = (id: number, spec: SpecificationDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/specifications/${id}`, spec);
    if (ok) {
      dispatch(alertSuccess(i18n.t('specification.deleted')));
      return dispatch(specificationActions.delete(spec));
    }
    return dispatch(specificationActions.requestFailure(error));
  };
};
